import React from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Collapse,
  Icon,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useDisclosure,
  Container,
  Heading,
  SimpleGrid,
  VStack,
  HStack,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaLinkedin, FaTwitter } from 'react-icons/fa';
import HeroSection from './components/HeroSection';
import AboutSection from './components/AboutSection';
import ServicesSection from './components/ServicesSection';
import ContactSection from './components/ContactSection';

function App() {
  const { isOpen, onToggle } = useDisclosure();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box>
      {/* Navigation Header */}
      <Box>
        <Flex
          bg={useColorModeValue('white', 'gray.800')}
          color={useColorModeValue('gray.600', 'white')}
          minH={'60px'}
          py={{ base: 2 }}
          px={{ base: 4 }}
          borderBottom={1}
          borderStyle={'solid'}
          borderColor={useColorModeValue('gray.200', 'gray.900')}
          align={'center'}
          position="fixed"
          top={0}
          left={0}
          right={0}
          zIndex={1000}
          boxShadow="sm"
        >
          <Flex
            flex={{ base: 1, md: 'auto' }}
            ml={{ base: -2 }}
            display={{ base: 'flex', md: 'none' }}
          >
            <IconButton
              onClick={onToggle}
              icon={
                isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
              }
              variant={'ghost'}
              aria-label={'Toggle Navigation'}
            />
          </Flex>
          <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'space-between' }}>
            <Text
              fontFamily={'heading'}
              color={useColorModeValue('brand.600', 'white')}
              fontSize="xl"
              fontWeight="bold"
              cursor="pointer"
              onClick={() => scrollToSection('hero')}
            >
              Mesh Infrastructure
            </Text>

            <Flex display={{ base: 'none', md: 'flex' }} ml={10}>
              <DesktopNav scrollToSection={scrollToSection} />
            </Flex>
          </Flex>

          <Stack
            flex={{ base: 1, md: 0 }}
            justify={'flex-end'}
            direction={'row'}
            spacing={6}
          >
            <Button
              as={'a'}
              display={{ base: 'none', md: 'inline-flex' }}
              fontSize={'sm'}
              fontWeight={600}
              color={'white'}
              bg={'brand.500'}
              href={'#contact'}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('contact');
              }}
              _hover={{
                bg: 'brand.600',
              }}
            >
              Get In Touch
            </Button>
          </Stack>
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <MobileNav scrollToSection={scrollToSection} />
        </Collapse>
      </Box>

      {/* Main Content */}
      <Box mt="60px">
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <ContactSection />
      </Box>

      {/* Footer */}
      <Box
        bg={useColorModeValue('gray.900', 'gray.900')}
        color={useColorModeValue('gray.200', 'gray.200')}
      >
        <Container maxW={'6xl'} py={10}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <VStack align="start">
              <Text fontSize="lg" fontWeight="bold" color="white">
                Mesh Infrastructure Group
              </Text>
              <Text fontSize="sm">
                Building Tomorrow's Infrastructure Today
              </Text>
            </VStack>
            <VStack align="start">
              <Text fontWeight="bold" color="white">Quick Links</Text>
              <Link onClick={() => scrollToSection('hero')}>Home</Link>
              <Link onClick={() => scrollToSection('about')}>About Us</Link>
              <Link onClick={() => scrollToSection('services')}>Services</Link>
              <Link onClick={() => scrollToSection('contact')}>Contact</Link>
            </VStack>
            <VStack align="start">
              <Text fontWeight="bold" color="white">Follow Us</Text>
              <HStack spacing={4}>
                <Icon as={FaLinkedin} boxSize={5} cursor="pointer" _hover={{ color: 'brand.400' }} />
                <Icon as={FaTwitter} boxSize={5} cursor="pointer" _hover={{ color: 'brand.400' }} />
              </HStack>
            </VStack>
          </SimpleGrid>
          <Divider my={6} borderColor="gray.700" />
          <Text textAlign="center" fontSize="sm">
            © 2024 Mesh Infrastructure Group. All rights reserved.
          </Text>
        </Container>
      </Box>
    </Box>
  );
}

const DesktopNav = ({ scrollToSection }) => {
  return (
    <Stack direction={'row'} spacing={4} align="center">
      <Link
        p={2}
        fontSize={'sm'}
        fontWeight={500}
        _hover={{ textDecoration: 'none', color: 'brand.500' }}
        onClick={() => scrollToSection('hero')}
      >
        Home
      </Link>
      <Link
        p={2}
        fontSize={'sm'}
        fontWeight={500}
        _hover={{ textDecoration: 'none', color: 'brand.500' }}
        onClick={() => scrollToSection('about')}
      >
        About Us
      </Link>
      <Menu>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          variant="ghost"
          size="sm"
          fontWeight={500}
        >
          What We Do
        </MenuButton>
        <MenuList>
          <MenuItem onClick={() => scrollToSection('services')}>All Services</MenuItem>
          <MenuItem>Infrastructure Planning</MenuItem>
          <MenuItem>Project Management</MenuItem>
          <MenuItem>Consulting Services</MenuItem>
        </MenuList>
      </Menu>
      <Link
        p={2}
        fontSize={'sm'}
        fontWeight={500}
        _hover={{ textDecoration: 'none', color: 'brand.500' }}
        onClick={() => scrollToSection('contact')}
      >
        Contact
      </Link>
    </Stack>
  );
};

const MobileNav = ({ scrollToSection }) => {
  return (
    <Stack
      bg={useColorModeValue('white', 'gray.800')}
      p={4}
      display={{ md: 'none' }}
    >
      <Link
        py={2}
        fontWeight={600}
        onClick={() => scrollToSection('hero')}
      >
        Home
      </Link>
      <Link
        py={2}
        fontWeight={600}
        onClick={() => scrollToSection('about')}
      >
        About Us
      </Link>
      <Link
        py={2}
        fontWeight={600}
        onClick={() => scrollToSection('services')}
      >
        What We Do
      </Link>
      <Link
        py={2}
        fontWeight={600}
        onClick={() => scrollToSection('contact')}
      >
        Contact
      </Link>
    </Stack>
  );
};

export default App;